import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import i18n from '@/utils/i18n';
import { useGetMerchantOrderSharedLinksForEventTableQuery } from '../../../graphql/queries/getMerchantOrderSharedLinksForEventTable.generated';
import { useGetEventQuery } from '../../../graphql/queries/getEvent.generated';
import {
  DefaultTable,
  DefaultTableHeader,
  DefaultTableHeaderRow,
  DefaultTableHeaderCell,
  DefaultTableBody,
  DefaultTableRow,
  DefaultTableCell,
} from '../../../components/DefaultTable';
import OrderStatusBadge from '../EventOrder/components/OrderStatusBadge';
import { Routes } from '@/utils/routes';
import { FaQuestionCircle, FaExternalLinkAlt } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { LinkCopiedSuccess } from '@/components/LinkCopiedSuccess';
import { toast } from '@/components/ui/sonner';
import { isDev, isLocalDev } from '@/utils/isDev';
import SvgIcon from '@/components/Icon/SvgIcon';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TablePagination } from '../../../components/TablePagination';
import { FilterSelect } from '../../../components/FilterSelect';
import {
  EVENT_ORDER_ALLOWED_STATUSES,
  getStatusLabelKeys,
} from '../EventOrder/components/orderStatusConfig';
import {
  MerchantOrderStatusEnum,
  MerchantOrderItemUnit,
} from '../../../graphql/generatedTypes';
import { Skeleton } from '../../../components/ui/skeleton';
import { ContainerCountCell } from '@/components/ContainerCountCell';

type EventOrdersTabProps = {
  eventId: string;
  eventName?: string | null;
};

export const EventOrdersTab_OLD: React.FC<EventOrdersTabProps> = ({
  eventId,
  eventName,
}) => {
  const location = useLocation();
  const history = useHistory();

  // Read URL parameters
  const searchParams = new URLSearchParams(location.search);
  const eventAccountIdFromUrl = searchParams.get('eventAccountId') || '';

  // Filter and pagination state
  const [selectedEventAccountId, setSelectedEventAccountId] = useState(
    eventAccountIdFromUrl,
  );
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State for tracking which link was copied
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  // Update filter when URL parameter changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eventAccountId = params.get('eventAccountId') || '';
    if (eventAccountId !== selectedEventAccountId) {
      setSelectedEventAccountId(eventAccountId);
      setCurrentPage(1);
      setCopiedLinkId(null); // Reset copied state when URL changes
    }
  }, [location.search]);

  // Fetch event participants for the filter
  const { data: eventData } = useGetEventQuery({
    variables: { id: eventId },
    fetchPolicy: 'cache-first',
  });

  // Build filter object
  const filter = useMemo(() => {
    const f: any = {};
    if (selectedEventAccountId) {
      f.event_account_id = selectedEventAccountId;
    }
    if (selectedStatus) {
      f.status = selectedStatus as MerchantOrderStatusEnum;
    }
    return Object.keys(f).length > 0 ? f : undefined;
  }, [selectedEventAccountId, selectedStatus]);

  // Calculate skip and take for server-side pagination
  const skip = (currentPage - 1) * rowsPerPage;
  const take = rowsPerPage;

  // Fetch data with server-side pagination
  const { data, loading, error } =
    useGetMerchantOrderSharedLinksForEventTableQuery({
      variables: {
        eventId,
        filter,
        skip,
        take,
      },
      fetchPolicy: 'network-only',
    });

  // Prepare event account options for filter
  const eventAccountOptions = useMemo(() => {
    const participants = eventData?.event?.event_participants || [];
    return participants.map((participant) => ({
      value: participant.id,
      label: participant.name || participant.id,
    }));
  }, [eventData]);

  // Prepare status options for filter (only allowed statuses)
  const statusOptions = useMemo(() => {
    const labelKeys = getStatusLabelKeys('admin');
    return EVENT_ORDER_ALLOWED_STATUSES.map((status) => {
      const labelKey = labelKeys[status] || 'EventOrder:statusOpen';
      return {
        value: status,
        label: i18n.t(labelKey),
      };
    });
  }, []);

  const response = data?.getMerchantOrderSharedLinksForEvent;
  const ordersWithData = response?.eventMerchantorderSharedLinks || [];
  const total = response?.total || 0;
  const containerTypes = data?.event?.allowed_container_types || [];

  // Create a map of container type ID to packing unit
  const containerTypeMap = useMemo(() => {
    const map = new Map<number, number>();
    containerTypes.forEach((ct) => {
      map.set(ct.id, ct.packing_unit || 1);
    });
    return map;
  }, [containerTypes]);

  // Calculate container counts for a merchant order
  const calculateContainerCounts = useMemo(() => {
    return (
      items:
        | Array<{
            type_id: number;
            amount_int: number;
            unit: MerchantOrderItemUnit;
          }>
        | null
        | undefined,
    ) => {
      if (!items || items.length === 0) {
        return { amountOfBoxes: 0, amountOfContainers: 0 };
      }

      let amountOfBoxes = 0;
      let amountOfContainers = 0;

      items.forEach((item) => {
        const packingUnit = containerTypeMap.get(item.type_id) || 1;

        if (item.unit === MerchantOrderItemUnit.Boxes) {
          // If unit is boxes: amount_int is boxes, convert to containers
          amountOfBoxes += item.amount_int;
          amountOfContainers += item.amount_int * packingUnit;
        } else {
          // If unit is pieces: amount_int is containers (pieces)
          amountOfContainers += item.amount_int;
          // Calculate boxes from containers
          const boxesFromContainers = Math.floor(item.amount_int / packingUnit);
          amountOfBoxes += boxesFromContainers;
        }
      });

      return { amountOfBoxes, amountOfContainers };
    };
  }, [containerTypeMap]);

  const ieMerchantOrderFormExpired = eventData?.event
    ?.merchant_order_form_deadline
    ? new Date(eventData?.event?.merchant_order_form_deadline) < new Date()
    : false;

  // Server-side pagination
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const paginatedOrders = ordersWithData;

  // Reset to page 1 when filters change and update URL
  const handleFilterChange =
    (setter: (value: string) => void, paramName?: string) =>
    (value: string) => {
      setter(value);
      setCurrentPage(1);
      setCopiedLinkId(null); // Reset copied state when filters change

      // Update URL parameter for event account filter
      if (paramName === 'eventAccountId') {
        const params = new URLSearchParams(location.search);
        if (value) {
          params.set('eventAccountId', value);
        } else {
          params.delete('eventAccountId');
        }
        history.replace({ search: params.toString() });
      }
    };

  // Handle copying link to clipboard
  const handleCopyLink = async (
    e: React.MouseEvent,
    sharedLinkId: string,
    eventAccountName: string | null,
  ) => {
    e.stopPropagation();

    const isDevEnvironment = isDev() || isLocalDev();
    const fullLink = `https://partner${isDevEnvironment ? '-dev' : ''}.vytal.org${Routes.eventOrder(sharedLinkId)}`;

    try {
      await navigator.clipboard.writeText(fullLink);
      setCopiedLinkId(sharedLinkId);

      // Show toast with event account name
      toast.success('Link copied to clipboard', {
        description: eventAccountName
          ? `Order form link for ${eventAccountName} has been copied`
          : 'Order form link has been copied',
      });
    } catch (error: any) {
      toast.error('Failed to copy link', {
        description: error.message || 'Unknown error',
      });
    }
  };

  return (
    <div className="tw:space-y-4">
      <div className="tw:flex tw:justify-between tw:items-center">
        <h2 className="tw:text-2xl tw:font-semibold">
          {i18n.t('MerchantOrder:eventOrders') || 'Event Orders'}
        </h2>
      </div>

      {/* Filters */}
      <div className="tw:flex tw:gap-2 tw:items-center">
        <FilterSelect
          value={selectedEventAccountId}
          onValueChange={handleFilterChange(
            setSelectedEventAccountId,
            'eventAccountId',
          )}
          options={eventAccountOptions}
          placeholder={i18n.t('Event:eventAccount') || 'Event Account'}
          emptyLabel={i18n.t('Common:all') || 'All'}
          disabled={loading}
        />
        <FilterSelect
          value={selectedStatus}
          onValueChange={handleFilterChange(setSelectedStatus)}
          options={statusOptions}
          placeholder={i18n.t('MerchantOrder:orderStatus') || 'Order Status'}
          emptyLabel={i18n.t('Common:all') || 'All'}
          disabled={loading}
        />
      </div>

      {error ? (
        <div className="tw:flex tw:justify-center tw:items-center tw:py-12">
          <p className="tw:text-red-500">
            {i18n.t('Common:error')}: {error.message}
          </p>
        </div>
      ) : ordersWithData.length === 0 && !loading ? (
        <div className="tw:flex tw:justify-center tw:items-center tw:py-12">
          <p className="tw:text-gray-500">
            {i18n.t('Event:noOrdersFound') ||
              'No orders found for this event yet.'}
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <DefaultTable>
            <DefaultTableHeader>
              <DefaultTableHeaderRow>
                <DefaultTableHeaderCell>
                  <div className="tw:flex tw:items-center tw:gap-2">
                    {i18n.t('Event:orderId') || 'Order ID'}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="tw:text-gray-400 tw:hover:text-gray-600">
                            <FaQuestionCircle className="tw:text-sm" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="tw:max-w-xs">
                            {i18n.t('Event:orderIdTooltip') ||
                              'The order ID is only unique for this event.'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </DefaultTableHeaderCell>
                <DefaultTableHeaderCell>
                  {i18n.t('Event:eventAccount') || 'Event Account'}
                </DefaultTableHeaderCell>
                <DefaultTableHeaderCell>
                  {i18n.t('MerchantOrder:orderStatus') || 'Order Status'}
                </DefaultTableHeaderCell>
                <DefaultTableHeaderCell>
                  {i18n.t('MerchantOrder:salesOrder') || 'Sales Order'}
                </DefaultTableHeaderCell>
                <DefaultTableHeaderCell>
                  {i18n.t('Event:numberOfContainers') || 'N° containers'}
                </DefaultTableHeaderCell>
                <DefaultTableHeaderCell className="tw:text-right"></DefaultTableHeaderCell>
              </DefaultTableHeaderRow>
            </DefaultTableHeader>
            <DefaultTableBody>
              {loading
                ? // Show skeleton rows while loading
                  Array.from({ length: rowsPerPage }).map((_, index) => (
                    <DefaultTableRow key={`skeleton-${index}`}>
                      <DefaultTableCell>
                        <Skeleton className="tw:h-4 tw:w-16" />
                      </DefaultTableCell>
                      <DefaultTableCell>
                        <Skeleton className="tw:h-4 tw:w-32" />
                      </DefaultTableCell>
                      <DefaultTableCell>
                        <Skeleton className="tw:h-6 tw:w-24" />
                      </DefaultTableCell>
                      <DefaultTableCell>
                        <Skeleton className="tw:h-4 tw:w-20" />
                      </DefaultTableCell>
                      <DefaultTableCell>
                        <Skeleton className="tw:h-4 tw:w-16" />
                      </DefaultTableCell>
                      <DefaultTableCell className="tw:text-right">
                        <Skeleton className="tw:h-8 tw:w-32 tw:ml-auto" />
                      </DefaultTableCell>
                    </DefaultTableRow>
                  ))
                : paginatedOrders.map((link) => {
                    const merchantOrder = link.merchant_order;
                    const deliveryNoteId = merchantOrder?.delivery_note_id;
                    const eventAccountName = link.event_account?.name || null;
                    const items = merchantOrder?.latest_revision?.containers;
                    const { amountOfBoxes, amountOfContainers } =
                      calculateContainerCounts(items);

                    return (
                      <DefaultTableRow key={link.id}>
                        <DefaultTableCell>
                          {deliveryNoteId ? (
                            <Link
                              to={Routes.editEventMerchantOrderSharedLink(
                                eventId,
                                link.id,
                              )}
                              className="tw:text-lilac-600 tw:underline tw:hover:text-lilac-700"
                            >
                              #{deliveryNoteId}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </DefaultTableCell>
                        <DefaultTableCell>
                          {link.event_account ? (
                            <Link
                              to={Routes.eventAccountDetail(
                                link.event_account.id,
                              )}
                              className="tw:text-lilac-600 tw:underline tw:hover:text-lilac-700"
                            >
                              {eventAccountName || '-'}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </DefaultTableCell>
                        <DefaultTableCell>
                          {merchantOrder ? (
                            <OrderStatusBadge
                              status={merchantOrder.status}
                              expired={ieMerchantOrderFormExpired}
                              variant="admin"
                            />
                          ) : (
                            <OrderStatusBadge
                              status={MerchantOrderStatusEnum.OnHold}
                              expired={ieMerchantOrderFormExpired}
                              variant="admin"
                            />
                          )}
                        </DefaultTableCell>
                        <DefaultTableCell>
                          {link.sales_order_url ? (
                            <a
                              href={link.sales_order_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tw:inline-flex tw:items-center tw:gap-1.5 tw:text-lilac-600 tw:underline tw:hover:text-lilac-700"
                            >
                              {i18n.t('MerchantOrder:viewInZoho') ||
                                'View in Zoho'}
                              <FaExternalLinkAlt className="tw:text-xs" />
                            </a>
                          ) : (
                            '-'
                          )}
                        </DefaultTableCell>
                        <DefaultTableCell>
                          <ContainerCountCell
                            amountOfBoxes={amountOfBoxes}
                            amountOfContainers={amountOfContainers}
                          />
                        </DefaultTableCell>
                        <DefaultTableCell className="tw:text-right tw:min-w-[220px]">
                          <div className="tw:flex tw:justify-end tw:min-h-[36px] tw:items-center">
                            {copiedLinkId === link.id ? (
                              <LinkCopiedSuccess />
                            ) : (
                              <Button
                                variant="outline"
                                onClick={(e) =>
                                  handleCopyLink(e, link.id, eventAccountName)
                                }
                                disabled={loading}
                              >
                                <SvgIcon name="copy-content" />
                                {i18n.t('Event:copyOrderFormLink') ||
                                  'Copy link to form'}
                              </Button>
                            )}
                          </div>
                        </DefaultTableCell>
                      </DefaultTableRow>
                    );
                  })}
            </DefaultTableBody>
          </DefaultTable>

          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={(newRowsPerPage) => {
              setRowsPerPage(newRowsPerPage);
              setCurrentPage(1);
            }}
            disabled={loading}
          />
        </>
      )}
    </div>
  );
};
